import React from 'react';
import { Skeleton, Step, StepButton, StepLabel, Stepper, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTranslation } from 'react-i18next';
import { useInterventionCreation } from './context/interventionCreationContext';
import { StepId } from './context/types';
import { useRequestsToAssign } from './Steps/Assignment/useRequestsToAssign';
import { useRequestsToQualify } from './Steps/Requests/useRequestsToQualify';

/**
 * L'index 1 est occupé par l'étape décorative « Tournées · RDV · Sous-traitance »,
 * qui n'est pas navigable : les étapes réelles sont donc en 0 et 2.
 */
const STEP_INDEXES: Record<StepId, number> = {
  requests: 0,
  assignment: 2
};

export default function CreationStepper() {
  const { t } = useTranslation();
  const { state, dispatch } = useInterventionCreation();
  // Appelés sans filtres : les compteurs annoncent le total de l'étape, pas le total
  // filtré. Tant que les panneaux n'ont ni filtre ni recherche, c'est la même clé de
  // requête que la leur, donc le même cache et aucun appel en double ; dès qu'un filtre
  // est posé, le stepper a sa propre entrée — et son propre premier chargement.
  const { count: toQualifyCount, isInitialLoading: isCountingToQualify } = useRequestsToQualify();
  const { count: toAssignCount, isInitialLoading: isCountingToAssign } = useRequestsToAssign();

  const hasClient = state.clientId !== null;

  /**
   * Les deux compteurs disent le total de leur étape, celles déjà traitées comprises :
   * une demande affectée reste « à affecter » puisqu'il lui reste l'envoi.
   *
   * Sans client, les deux requêtes sont désactivées : un « 0 » y serait faux. Il le serait
   * tout autant avant la première réponse, d'où le squelette — `count` vaut 0 tant que
   * rien n'est connu, et un changement de client repasse par cet état faute de
   * `keepPreviousData`.
   *
   * Le squelette vit dans le `Typography` : en `variant="text"` il déduit sa hauteur des
   * métriques de la police du parent, la ligne garde donc sa hauteur et rien ne saute
   * quand le chiffre arrive.
   *
   * `wave` plutôt que le `pulse` par défaut : le reflet qui balaie se lit comme un travail
   * en cours, là où le fondu passe pour un élément grisé. Même choix que `Suspended`.
   */
  const stepCount = (labelKey: string, total: number, isCounting: boolean) =>
    hasClient ? (
      <Typography variant="caption" color="text.secondary">
        {isCounting ? (
          <Skeleton variant="text" width={72} animation="wave" />
        ) : (
          t(labelKey, { total })
        )}
      </Typography>
    ) : undefined;

  return (
    <Stepper nonLinear activeStep={STEP_INDEXES[state.currentStep]}>
      <Step>
        <StepButton
          disabled={!hasClient}
          optional={stepCount(
            'interventionCreation.steps.requestsCount',
            toQualifyCount,
            isCountingToQualify
          )}
          onClick={() => dispatch({ type: 'goToStep', step: 'requests' })}
        >
          {t('interventionCreation.steps.requests')}
        </StepButton>
      </Step>
      <Step disabled>
        <StepLabel
          icon={<MoreHorizIcon fontSize="small" />}
          // Seule étape sans numéro, et le libellé le plus long de la barre : aligné en
          // ligne, il part en drapeau à droite d'une icône de 20px. Le mode colonne pose
          // icône, libellé et mention sur un axe centré — c'est `alternativeLabel` du
          // Stepper, restreint à cette étape pour ne pas déplacer les deux navigables.
          sx={{
            flexDirection: 'column',
            '& .MuiStepLabel-iconContainer': { pr: 0 },
            '& .MuiStepLabel-labelContainer': { textAlign: 'center' }
          }}
          optional={
            <Typography variant="caption" color="text.secondary">
              {t('interventionCreation.steps.logisticsHint')}
            </Typography>
          }
        >
          {t('interventionCreation.steps.logistics')}
        </StepLabel>
      </Step>
      <Step>
        <StepButton
          disabled={!hasClient}
          // MUI numérote sur la position dans le stepper, or l'étape décorative qui
          // précède occupe l'index 1 sans afficher de numéro : sans cela, la deuxième
          // étape réelle s'annoncerait « 3 ».
          icon={2}
          optional={stepCount(
            'interventionCreation.steps.assignmentCount',
            toAssignCount,
            isCountingToAssign
          )}
          onClick={() => dispatch({ type: 'goToStep', step: 'assignment' })}
        >
          {t('interventionCreation.steps.assignment')}
        </StepButton>
      </Step>
    </Stepper>
  );
}
